import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Heart, X, Star, MapPin, CheckCircle2, Verified } from "lucide-react";
import { motion, AnimatePresence, useMotionValue, useTransform } from "framer-motion";
import { tx } from "../lib/tx";

/**
 * Mode Bulle pour la découverte de profils
 * Affichage horizontal scrollable avec profils en ligne/vérifiés en premier
 */
export default function BubbleView({ 
  profiles, 
  onLike, 
  onDislike, 
  onSuperLike,
  onClose 
}) {
  const navigate = useNavigate();
  const scrollRef = useRef(null);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);

  // Trier les profils : en ligne d'abord, puis vérifiés, puis par distance
  const sortedProfiles = [...profiles].sort((a, b) => {
    // En ligne en premier
    if (a.is_online && !b.is_online) return -1;
    if (!a.is_online && b.is_online) return 1;
    // Vérifiés ensuite
    if (a.is_verified && !b.is_verified) return -1;
    if (!a.is_verified && b.is_verified) return 1;
    // Par distance
    return (a.distance || 999) - (b.distance || 999);
  });

  const handleAction = async (action, profile) => {
    setActionLoading(profile.user_id);
    try {
      if (action === 'like') {
        await onLike(profile.user_id);
      } else if (action === 'dislike') {
        await onDislike(profile.user_id);
      } else if (action === 'super_like') {
        await onSuperLike(profile.user_id);
      }
    } catch (error) {
      console.error("Erreur action:", error);
    }
    setActionLoading(null);
    setSelectedProfile(null);
  };

  const handleProfileClick = (profile) => {
    setSelectedProfile(profile);
  };

  return (
    <div className="fixed inset-0 z-40 bg-black/95 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-white/10">
        <h2 className="text-lg font-semibold text-white">{tx("Mode Bulle")}</h2>
        <button
          onClick={onClose}
          className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Bulles scrollables */}
      <div 
        ref={scrollRef}
        className="flex-1 overflow-x-auto overflow-y-hidden py-8 px-4 scrollbar-hide"
        style={{ scrollBehavior: 'smooth' }}
      >
        <div className="flex gap-4 h-full items-center min-w-max">
          {sortedProfiles.map((profile, index) => (
            <motion.div
              key={profile.user_id}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: index * 0.05 }}
              className="flex flex-col items-center"
            >
              {/* Bulle du profil */}
              <motion.button
                onClick={() => handleProfileClick(profile)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className={`relative w-24 h-24 md:w-28 md:h-28 rounded-full overflow-hidden border-4 transition-all ${
                  profile.is_online 
                    ? 'border-green-500 shadow-[0_0_20px_rgba(34,197,94,0.5)]' 
                    : profile.is_verified 
                      ? 'border-blue-500'
                      : 'border-white/30'
                }`}
              >
                <img
                  src={profile.main_photo || profile.photos?.[0] || profile.picture}
                  alt={profile.name}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.src = `https://ui-avatars.com/api/?name=${profile.name}&size=128&background=random`;
                  }}
                />
                
                {/* Badge en ligne */}
                {profile.is_online && (
                  <div className="absolute bottom-1 right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-black" />
                )}
                
                {/* Badge vérifié */}
                {profile.is_verified && (
                  <div className="absolute top-1 right-1 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
                    <CheckCircle2 className="w-3 h-3 text-white" />
                  </div>
                )}
              </motion.button>

              {/* Nom et infos */}
              <div className="mt-2 text-center">
                <p className="text-white font-medium text-sm truncate max-w-24">
                  {profile.name}, {profile.age}
                </p>
                {profile.distance !== null && profile.distance !== undefined && (
                  <p className="text-muted-foreground text-xs flex items-center justify-center gap-1">
                    <MapPin className="w-3 h-3" />
                    {profile.distance} km
                  </p>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Popup profil sélectionné */}
      <AnimatePresence>
        {selectedProfile && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-end justify-center bg-black/80"
            onClick={() => setSelectedProfile(null)}
          >
            <motion.div
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="bg-card rounded-t-3xl w-full max-w-lg overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Image du profil */}
              <div className="relative h-64">
                <img
                  src={selectedProfile.main_photo || selectedProfile.photos?.[0] || selectedProfile.picture}
                  alt={selectedProfile.name}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                
                {/* Infos sur l'image */}
                <div className="absolute bottom-4 left-4 right-4">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-2xl font-bold text-white">
                      {selectedProfile.name}, {selectedProfile.age}
                    </h3>
                    {selectedProfile.is_verified && (
                      <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                        <Verified className="w-4 h-4 text-white" />
                      </div>
                    )}
                    {selectedProfile.is_online && (
                      <span className="px-2 py-0.5 bg-green-500 text-white text-xs font-medium rounded-full">
                        {tx("En ligne")}
                      </span>
                    )}
                  </div>
                  {selectedProfile.location && (
                    <p className="text-white/80 text-sm flex items-center gap-1">
                      <MapPin className="w-4 h-4" />
                      {selectedProfile.location}
                      {selectedProfile.distance && ` • ${selectedProfile.distance} km`}
                    </p>
                  )}
                </div>
                
                {/* Bouton fermer */}
                <button
                  onClick={() => setSelectedProfile(null)}
                  className="absolute top-4 right-4 w-8 h-8 rounded-full bg-black/50 flex items-center justify-center text-white"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Bio */}
              {selectedProfile.bio && (
                <div className="p-4 border-b border-white/10">
                  <p className="text-white/80 text-sm">{selectedProfile.bio}</p>
                </div>
              )}

              {/* Intérêts */}
              {selectedProfile.interests?.length > 0 && (
                <div className="p-4 border-b border-white/10">
                  <div className="flex flex-wrap gap-2">
                    {selectedProfile.interests.slice(0, 5).map((interest, i) => (
                      <span 
                        key={i}
                        className="px-3 py-1 bg-white/10 text-white text-xs rounded-full"
                      >
                        {interest}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Boutons d'action */}
              <div className="p-4 flex justify-center gap-4">
                {/* Dislike */}
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => handleAction('dislike', selectedProfile)}
                  disabled={actionLoading === selectedProfile.user_id}
                  className="w-14 h-14 rounded-full bg-white/10 border border-white/20 flex items-center justify-center text-white hover:bg-red-500/20 hover:border-red-500/50 transition-colors"
                >
                  <X className="w-7 h-7" />
                </motion.button>

                {/* Super Like */}
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => handleAction('super_like', selectedProfile)}
                  disabled={actionLoading === selectedProfile.user_id}
                  className="w-14 h-14 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center text-white shadow-lg"
                >
                  <Star className="w-7 h-7 fill-current" />
                </motion.button>

                {/* Like */}
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => handleAction('like', selectedProfile)}
                  disabled={actionLoading === selectedProfile.user_id}
                  className="w-14 h-14 rounded-full bg-gradient-to-r from-pink-500 to-rose-500 flex items-center justify-center text-white shadow-lg"
                >
                  {actionLoading === selectedProfile.user_id ? (
                    <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Heart className="w-7 h-7 fill-current" />
                  )}
                </motion.button>
              </div>

              {/* Voir le profil complet */}
              <div className="p-4 pt-0">
                <button
                  onClick={() => navigate(`/profile/${selectedProfile.user_id}`)}
                  className="w-full py-3 bg-white/10 text-white font-medium rounded-xl hover:bg-white/20 transition-colors"
                >
                  {tx("Voir le profil complet")}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Indicateur de scroll */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/50 text-xs flex items-center gap-2">
        <span>←</span>
        {tx("Faites défiler pour voir plus de profils")}
        <span>→</span>
      </div>
    </div>
  );
}
